import {isArray, isFinite, isString, noop, omit} from 'lodash';

import {readApiModel} from '../../libs/shared/core/src/helpers/api/apiHelpers';
import {inAnimationFrameOrHiddenCallbackAsync} from '../../libs/shared/core/src/helpers/async/inAnimationFrame';
import {TaskSourcesEnum} from '../../libs/shared/core/src/helpers/instrumentation/eventLoopPerformanceHelpers';
import {ErrorWithData} from '../../libs/shared/core/src/helpers/types/customErrors';
import {ApiChannelDataGroup} from '../../libs/shared/core/src/models/api/commonDataGroupModel';
import {ApiCompany} from '../../libs/shared/core/src/models/api/company/companyModel';
import {ApiInbox} from '../../libs/shared/core/src/models/api/inboxModel';
import {ApiJob, isKnownJobType} from '../../libs/shared/core/src/models/api/jobModel';
import {parseKnowledgeBase} from '../../libs/shared/core/src/models/api/knowledgeBaseModel';
import {parseRuleBundleInfoLight} from '../../libs/shared/core/src/models/api/ruleBundleInfo';
import {ApiStashPush} from '../../libs/shared/core/src/models/api/stash/stashModel';
import {ApiTeammate} from '../../libs/shared/core/src/models/api/teammateModel';
import {ApiTeam} from '../../libs/shared/core/src/models/api/teamModel';
import {reportToBugsnag} from '../../libs/shared/core/src/services/bugsnag/bugsnag';
import {sessionId} from '../../libs/shared/core/src/services/http/session';
import {logger} from '../../libs/shared/core/src/services/logger/logger';
import {PushEventDefinition} from '../../libs/shared/core/src/services/push/pushTypes';
import {
  jobDeletedAction,
  jobUpdatedAction,
} from '../../libs/shared/core/src/store/data/jobs/commonJobsActions';
import {
  knowledgeBaseCreatedAction,
  knowledgeBaseUpdatedAction,
} from '../../libs/shared/core/src/store/data/knowledgeBases/knowledgeBasesActions';
import {pushSlaBundleUpdatedAsyncAction} from '../../libs/shared/core/src/store/data/slaRuleBundleInfo/slaRuleBundleInfoActions';
import {stashFetchedFromServerAction} from '../../libs/shared/core/src/store/data/stash/stashActions';
import {ApiAccount} from '../../src/models/api/accountModel';
import {ApiApplicationEntryPointAuthenticationStatus} from '../../src/models/api/applicationEntryPointModel';
import {ApiApplication} from '../../src/models/api/applicationModel';
import {ApiApplicationPreferences} from '../../src/models/api/applicationPreferencesModel';
import {ApiApplicationSettings} from '../../src/models/api/applicationSettingsModel';
import {ApiCardGroup} from '../../src/models/api/cardGroupModel';
import {ApiCard} from '../../src/models/api/cardModel';
import {ApiCardNote} from '../../src/models/api/cardNoteModel';
import {ApiCircle} from '../../src/models/api/circleModel';
import {CommentStatusesEnum} from '../../src/models/api/commentModel';
import {parseConversationLastMessage} from '../../src/models/api/conversationModel';
import {ApiConversationUpdatedThinEvent} from '../../src/models/api/conversationUpdatedThinEventModel';
import {ApiBadgeCounters, ApiBoldCounters} from '../../src/models/api/countersModel';
import {ApiCrmSync} from '../../src/models/api/crmSyncModel';
import {ApiCustomField} from '../../src/models/api/customFieldModel';
import {ApiCustomViews} from '../../src/models/api/customViewModel';
import {
  ApiActivityDataGroup,
  ApiCommentDataGroup,
  ApiWorkspaceDataGroup,
} from '../../src/models/api/dataGroupModel';
import {
  ApiImpersonationRequest,
  ImpersonationRequestModel,
} from '../../src/models/api/impersonationRequestModel';
import {ApiIntegrationError} from '../../src/models/api/integrationErrorModel';
import {ApiIntegration} from '../../src/models/api/integrationModel';
import {ApiLegacyApplication} from '../../src/models/api/legacyApplicationModel';
import {ApiLens} from '../../src/models/api/lensModel';
import {ApiMacro, parseMacroExecutionInfo} from '../../src/models/api/macroModel';
import {
  parseConversationPrecog,
  parseConversationPrecogId,
  parseConversationSentPrecog,
  PrecogModel,
  TypingPrecogModel,
} from '../../src/models/api/precogModel';
import {ApiPreferences} from '../../src/models/api/preferencesModel';
import {ApiPushDelegation} from '../../src/models/api/pushDelegationModel';
import {ApiCustomRole} from '../../src/models/api/roleModel';
import {ApiSignature} from '../../src/models/api/signatureModel';
import {ApiTag} from '../../src/models/api/tagModel';
import {ApiTeammateInboxesUpdate} from '../../src/models/api/teammateInboxesUpdateModel';
import {ApiTopic} from '../../src/models/api/topicModel';
import {ApiUserMetadata} from '../../src/models/api/userMetadataModel';
import {logToLogstash} from '../../src/services/logstash';
import {deletePrecog} from '../../src/services/precogs';
import {pushActivityDeletedTask} from '../../src/services/tasks/pushActivityDeletedTask';
import {pushMessageDeletedTask} from '../../src/services/tasks/pushMessageDeletedTask';
import {pushMessageUpdatedTask} from '../../src/services/tasks/pushMessageUpdatedTask';
import {pushPreferencesUpdatedTask} from '../../src/services/tasks/pushPreferencesUpdatedTask';
import {Dispatch} from '../../src/store/appStore';
import {updateAccountAction} from '../../src/store/data/accounts/accountsActions';
import {
  createActivityFromPushAsyncAction,
  updateActivityFromPushAsyncAction,
} from '../../src/store/data/activities/activitiesActions';
import {
  deleteApplicationAction,
  updateApplicationAction,
  updateApplicationEntryPointAuthenticationStatusAction,
} from '../../src/store/data/applications/applicationsActions';
import {updateApplicationSettingsAction} from '../../src/store/data/applications/applicationSettingsActions';
import {
  deleteLegacyApplication,
  updateLegacyApplication,
  updateLegacyApplicationPreferences,
} from '../../src/store/data/applications/legacyApplicationsActions';
import {updateCardGroupAction} from '../../src/store/data/cardGroups/cardGroupsActions';
import {updateCardNoteAction} from '../../src/store/data/cardNotes/cardNotesActions';
import {updateCardAction} from '../../src/store/data/cards/cardsActions';
import {channelDeletedAction} from '../../src/store/data/channels/channelsActions';
import {deleteCircleAction} from '../../src/store/data/circles/circlesActions';
import {commentDeletedAction} from '../../src/store/data/comments/commentsActions';
import {companyUpdatedAction} from '../../src/store/data/company/companyActions';
import {
  conversationDeletedAction,
  conversationSplitAction,
} from '../../src/store/data/conversations/conversationsActions';
import {
  pushConversationPrecogsUpdatedAction,
  pushConversationsJoinedAsyncAction,
  pushPartialConversationUpdatedAction,
} from '../../src/store/data/conversations/pushConversationsActions';
import {
  partialUpdateBadgeCounters,
  partialUpdateBadgeUnreadCounters,
  partialUpdateBoldCounters,
  partialUpdateDelegationCounters,
} from '../../src/store/data/counters/countersActions';
import {deleteCrmSyncAction} from '../../src/store/data/crmSync/crmSyncsActions';
import {
  createdCustomEmojiAction,
  deletedCustomEmojiAction,
} from '../../src/store/data/customEmojis/customEmojiActions';
import {
  deleteCustomFieldAction,
  updateCustomFieldAction,
} from '../../src/store/data/customFields/customFieldsActions';
import {setCustomViews} from '../../src/store/data/customViews/customViewsActions';
import {dataUpdatedAction} from '../../src/store/data/dataGroupsActions';
import {deleteDelegation, updateDelegation} from '../../src/store/data/delegations/delegationsActions';
import {
  pushInboxDeleted,
  updateInboxAction,
  updateTeammateInboxesAction,
} from '../../src/store/data/inboxes/inboxesActions';
import {fetchInquiryListsAsyncAction} from '../../src/store/data/inquiryLists/inquiryListsThunkActions';
import {
  deleteIntegrationErrorAction,
  updateIntegrationErrorAction,
} from '../../src/store/data/integrations/integrationErrorsActions';
import {
  deleteIntegrationAction,
  updateIntegrationAction,
  updateIntegrationTeammateSettingsAction,
} from '../../src/store/data/integrations/integrationsActions';
import {deleteLensAction, updateLensAction} from '../../src/store/data/lenses/saveLensActions';
import {deleteMacroAction} from '../../src/store/data/macros/macroActions';
import {
  updateConversationPrecog,
  updateConversationPrecogs,
} from '../../src/store/data/precogs/conversationPrecogsActions';
import {deleteRole, updateRole} from '../../src/store/data/roles/rolesActions';
import {signatureDeletedAction, updateSignature} from '../../src/store/data/signatures/signaturesActions';
import {
  pushTagDeletedAction,
  pushTagsBulkAction,
  pushTagUpdatedAction,
} from '../../src/store/data/tags/pushTagActions';
import {pushTeammateUpdatedAction} from '../../src/store/data/teammates/teammatesActions';
import {deleteTeam, updateTeam} from '../../src/store/data/teams/teamsActions';
import {updateTopicAction} from '../../src/store/data/topics/topicsActions';
import {updateUserMetadataAction} from '../../src/store/data/userMetadata/userMetadataActions';
import {pushWorkspaceUpdatedAsyncAction} from '../../src/store/data/workspace/pushWorkspaceActions';
import {showMacroSuccessBannerAction} from '../../src/store/panels/app/banners/bannersActions';
import {computeShouldAcceptConversationUpdatedThinEventAction} from '../../src/store/panels/cells/cellsPanelActions';
import {PushCoordinator} from './pushCoordinator';
import {PushBaseProps} from './pushTypes';

const pushLogger = logger.getLogger('push');

/*
 * Types.
 */

/** Only for events updating the component internal state */
export interface PushEventSubscriptionsDelegate {
  enqueueDelayedRenderTask: PushCoordinator['enqueueDelayedRenderTask'];
  shouldDiscardServerPrecog: PushCoordinator['shouldDiscardServerPrecog'];
  onFetchUpdatedConversation: PushCoordinator['onFetchUpdatedConversation'];
  refreshContent: PushCoordinator['refreshContent'];
  onDraftFailed: (failedDraftDescription: string) => void;
  onReceiveImpersonationRequest: (impersonationRequest: ImpersonationRequestModel) => void;
}

/**
 * @param  {PushBaseProps} props The properties to configure push event handlers
 * @param {PushEventSubscriptionsDelegate} subscriptionsDelegate: An object with all callbacks used in response to push events
 * @returns {ReadonlyArray<PushEventDefinition>} An array containing all push event definitions.
 */
export function makePushEventSubscriptions(
  props: PushBaseProps,
  subscriptionsDelegate: PushEventSubscriptionsDelegate,
  dispatch: Dispatch,
): ReadonlyArray<PushEventDefinition> {
  return [
    {
      name: 'partial_counters_updated',
      handler: (data) => {
        if (data.type === 'open') {
          const badgeCounters = readApiModel(ApiBadgeCounters, data);

          // If delegation_id is present, assume that the counters pertain to the corresponding delegation.
          const delegationId = data.delegation_id;
          if (Number.isInteger(delegationId)) {
            return () => dispatch(partialUpdateDelegationCounters(delegationId, badgeCounters));
          }

          return () => dispatch(partialUpdateBadgeCounters(badgeCounters));
        }
        if (data.type === 'unread') {
          const badgeUnreadCounters = readApiModel(ApiBadgeCounters, data);
          return () => dispatch(partialUpdateBadgeUnreadCounters(badgeUnreadCounters));
        }
        if (data.type === 'unseen') {
          const boldCounters = readApiModel(ApiBoldCounters, data);
          return () => dispatch(partialUpdateBoldCounters(boldCounters));
        }

        return () => {};
      },
      onReceiveHandler: (data) => {
        try {
          const shouldLogCounterEvent = data.counters_to_verbosely_log?.length > 0 && data.version === 2;

          if (shouldLogCounterEvent) {
            const {counters} = data;
            for (const {counter, field} of data.counters_to_verbosely_log || []) {
              if (counters[field]) {
                const {value, uid} = counters[field];
                logToLogstash({
                  action: 'counters-push-received',
                  counter,
                  value,
                  uid,
                  received_at: new Date().toISOString(),
                  teammate_id: props.user.id,
                  session_id: sessionId,
                });
              }
            }
          }
        } catch (err) {
          pushLogger.error('Dropping verbose counter logs', {
            counters_payload_version: data.version,
            message: err.message,
          });
        }
      },
    },
    {
      name: 'unread_partial_counters_updated',
      handler: (data) => {
        const badgeUnreadCounters = readApiModel(ApiBadgeCounters, data);
        return () => dispatch(partialUpdateBadgeUnreadCounters(badgeUnreadCounters));
      },
    },
    {
      name: 'unseen_partial_counters_updated',
      handler: (data) => {
        const boldCounters = readApiModel(ApiBoldCounters, data);
        return () => dispatch(partialUpdateBoldCounters(boldCounters));
      },
    },
    {
      name: 'open_delegation_partial_counters_updated',
      handler: (data) => {
        const delegationId = data.delegation_id;
        if (!Number.isInteger(delegationId)) {
          return noop;
        }

        const delegationCounters = readApiModel(ApiBadgeCounters, data);
        return () => dispatch(partialUpdateDelegationCounters(delegationId, delegationCounters));
      },
    },
    {
      name: 'company_updated',
      handler: (data) => {
        const updatedCompany = readApiModel(ApiCompany, data);
        return () => dispatch(companyUpdatedAction(updatedCompany));
      },
    },
    {
      name: 'team_updated',
      handler: (data) => {
        const team = readApiModel(ApiTeam, data);
        return () => dispatch(updateTeam(team));
      },
    },
    {
      name: 'team_deleted',
      handler: (data) => {
        const teamId = Number(data);
        return () => dispatch(deleteTeam(teamId));
      },
    },
    {
      name: 'teammate_updated',
      handler: (data) => {
        const teammate = data.class ? readApiModel(ApiTeammate, data) : undefined;

        const preferences =
          data.class && data.preferences && data.id === props.user.id
            ? readApiModel(ApiPreferences, data)
            : undefined;

        const metadata =
          data.class && data.metadata && data.id === props.user.id
            ? readApiModel(ApiUserMetadata, data)
            : undefined;

        return () => {
          if (teammate) {
            dispatch(pushTeammateUpdatedAction(teammate));
          }

          if (preferences) {
            dispatch(pushPreferencesUpdatedTask(preferences));
          }

          if (metadata) {
            dispatch(updateUserMetadataAction(metadata));
          }
        };
      },
    },
    {
      name: 'circle_updated',
      handler: (data) => {
        const circle = readApiModel(ApiCircle, data);
        return () => dispatch(dataUpdatedAction({circles: [circle]}));
      },
    },
    {
      name: 'circle_deleted',
      handler: (data) => {
        const circleId = Number(data);
        return () => dispatch(deleteCircleAction(circleId));
      },
    },
    {
      name: 'p1_updated',
      handler: (data) => {
        const customViews = readApiModel(ApiCustomViews, data);
        return () => dispatch(setCustomViews(customViews));
      },
    },
    {
      name: 'workspace_updated',
      handler: (data) => {
        const workspaceDataGroup = readApiModel(ApiWorkspaceDataGroup, data);
        return () => dispatch(pushWorkspaceUpdatedAsyncAction(workspaceDataGroup));
      },
    },
    {
      name: 'teammate_inboxes_updated',
      handler: (data) => {
        const update = readApiModel(ApiTeammateInboxesUpdate, data);
        return () => dispatch(updateTeammateInboxesAction(update));
      },
    },
    {
      name: 'inbox_updated',
      handler: (data) => {
        const inbox = readApiModel(ApiInbox, data);
        return () => dispatch(updateInboxAction(inbox));
      },
    },
    {
      name: 'inbox_deleted',
      handler: (data) => {
        const inboxId = Number(data);
        return () => dispatch(pushInboxDeleted(inboxId));
      },
    },
    {
      name: 'channel_updated',
      handler: (data) => {
        const channelDataGroup = readApiModel(ApiChannelDataGroup, data);
        return () => dispatch(dataUpdatedAction(channelDataGroup));
      },
    },
    {
      name: 'channel_deleted',
      handler: (data) => {
        const channelId = Number(data);
        return () => dispatch(channelDeletedAction(channelId));
      },
    },
    {
      name: 'signature_updated',
      handler: (data) => {
        const signature = readApiModel(ApiSignature, data);
        return () => dispatch(updateSignature(signature));
      },
    },
    {
      name: 'signature_deleted',
      handler: (data) => {
        const signatureId = Number(data);
        return () => dispatch(signatureDeletedAction(signatureId));
      },
    },
    {
      name: 'role_updated',
      handler: (data) => {
        const role = readApiModel(ApiCustomRole, data);
        return () => dispatch(updateRole(role));
      },
    },
    {
      name: 'role_deleted',
      handler: (data) => {
        const roleAlias = String(data);
        return () => dispatch(deleteRole(roleAlias));
      },
    },
    {
      name: 'delegation_updated',
      handler: (data) => {
        // If this is a counters-only update, nothing to do.
        if (data.isCountersUpdate) {
          return noop;
        }

        // Otherwise, update the state.
        const pushDelegation = readApiModel(ApiPushDelegation, data);
        return () => dispatch(updateDelegation(pushDelegation));
      },
    },
    {
      name: 'delegation_deleted',
      handler: (data) => {
        const id = Number(data);
        return () => dispatch(deleteDelegation(id));
      },
    },
    {
      name: 'custom_field_updated',
      handler: (data) => {
        const customField = readApiModel(ApiCustomField, data);
        return () => dispatch(updateCustomFieldAction(customField));
      },
    },
    {
      name: 'custom_field_deleted',
      handler: (data) => {
        const id = Number(data);
        return () => dispatch(deleteCustomFieldAction(id));
      },
    },
    {
      name: 'integration_updated',
      handler: (data) => {
        const integration = readApiModel(ApiIntegration, data);
        return () => dispatch(updateIntegrationAction(integration));
      },
    },
    {
      name: 'integration_deleted',
      handler: (data) => {
        const id = Number(data);
        return () => dispatch(deleteIntegrationAction(id));
      },
    },
    {
      name: 'integration_teammate_settings_updated',
      handler: (data) => {
        const integrationId = Number(data.integration_id);
        const integrationTeammateSettings = Object(data.teammate_settings);

        if (!Number.isInteger(integrationId) || !integrationTeammateSettings) {
          return noop;
        }

        return () =>
          dispatch(updateIntegrationTeammateSettingsAction(integrationId, integrationTeammateSettings));
      },
    },
    {
      name: 'job_updated',
      handler: (data) => {
        if (!data || !isKnownJobType(data.type)) {
          return noop;
        }

        const job = readApiModel(ApiJob, data);
        return () => dispatch(jobUpdatedAction(job));
      },
    },
    {
      name: 'job_deleted',
      handler: (data) => {
        const id = Number(data);
        return () => dispatch(jobDeletedAction(id));
      },
    },
    {
      name: 'application_updated',
      handler: (data) => {
        const application = readApiModel(ApiLegacyApplication, data);
        return () => dispatch(updateLegacyApplication(application));
      },
    },
    {
      name: 'application_deleted',
      handler: (data) => {
        const id = String(data);
        return () => dispatch(deleteLegacyApplication(id));
      },
    },
    {
      name: 'application_setting_created',
      handler: (data) => {
        const applicationSettings = readApiModel(ApiApplicationSettings, data);
        return () => dispatch(updateApplicationSettingsAction(applicationSettings));
      },
    },
    {
      name: 'application_setting_updated',
      handler: (data) => {
        const applicationSettings = readApiModel(ApiApplicationSettings, data);
        return () => dispatch(updateApplicationSettingsAction(applicationSettings));
      },
    },
    {
      name: 'application_settings_updated',
      handler: (data) => {
        const applicationPreferences = readApiModel(ApiApplicationPreferences, data);
        return () =>
          dispatch(updateLegacyApplicationPreferences(Number(data.application_id), applicationPreferences));
      },
    },
    {
      name: 'tag_updated',
      handler: (data) => {
        const tag = readApiModel(ApiTag, data);
        return () => dispatch(pushTagUpdatedAction(tag));
      },
    },
    {
      name: 'tag_id_deleted',
      handler: (data) => {
        const tagId = Number(data);
        return () => dispatch(pushTagDeletedAction(tagId));
      },
    },
    {
      name: 'tags_updated_batch',
      handler: (data) => {
        const tagIds = Array.from(data.tag_ids || [])
          .filter((id) => Number.isInteger(id))
          .map((id) => Number(id));

        return () => dispatch(pushTagsBulkAction(tagIds, false));
      },
    },
    {
      name: 'tags_deleted_batch',
      handler: (data) => {
        const tagIds = Array.from(data.tag_ids || [])
          .filter((id) => Number.isInteger(id))
          .map((id) => Number(id));

        return () => dispatch(pushTagsBulkAction(tagIds, true));
      },
    },
    {
      name: 'account_updated',
      handler: (data) => {
        const account = readApiModel(ApiAccount, data);
        return () => dispatch(updateAccountAction(account, true));
      },
    },
    {
      name: 'card_updated',
      handler: (data) => {
        const card = readApiModel(ApiCard, data);
        // Pass true to only update cards that already exist in the store and ignore all others.
        return () => dispatch(updateCardAction(card, true));
      },
    },
    {
      name: 'card_group_updated',
      handler: (data) => {
        const cardGroup = readApiModel(ApiCardGroup, data);
        return () => dispatch(updateCardGroupAction(cardGroup));
      },
    },
    {
      name: 'card_note_updated',
      handler: (data) => {
        const cardNote = readApiModel(ApiCardNote, data);
        return () => dispatch(updateCardNoteAction(cardNote));
      },
    },
    {
      name: 'conversation_updated_thin',
      handler: (data) => {
        if (!shouldAcceptConversationUpdatedThinPushEvent(data, props, dispatch)) {
          return noop;
        }

        const conversationId = Number(data && data.id);
        const shouldDiscardServerPrecog = subscriptionsDelegate.shouldDiscardServerPrecog(
          conversationId,
          data.is_precogs_already_sent_directly,
        );

        // Try to refetch the conversation.
        const conversationUrl = data && data.url;
        if (!isString(conversationUrl)) {
          reportToBugsnag(new ErrorWithData(`Missing conversation URL in conversation_updated_thin`, {data}));
          return noop;
        }

        const fetchOptions = {
          conversationId,
          conversationUrl,
          shouldDiscardServerPrecog,
          requesterId: Number(data.requester_id) || undefined,
        };
        subscriptionsDelegate.onFetchUpdatedConversation(fetchOptions);

        // No need to update the precogs if we keep the previous ones.
        let allTypingPrecogs: Array<TypingPrecogModel>;
        if (!shouldDiscardServerPrecog) {
          allTypingPrecogs = data.precogs
            .map((p: any) => parseConversationPrecog(conversationId, p))
            .filter((p: PrecogModel) => p.teammateId !== props.user.id);
        }

        // Never discard them because they always come from the server, never directly.
        const allSentPrecogs = (data.sentPrecogs || [])
          .map((p: any) => parseConversationSentPrecog(conversationId, p))
          .filter((p: PrecogModel) => p.teammateId !== props.user.id);

        return () => dispatch(updateConversationPrecogs(conversationId, allTypingPrecogs, allSentPrecogs));
      },
    },
    {
      name: 'conversation_updated_partial',
      handler: (data) => {
        const conversationId = Number(data && data.id);

        const shouldDiscardServerPrecog = subscriptionsDelegate.shouldDiscardServerPrecog(
          conversationId,
          data.is_precogs_already_sent_directly,
        );
        const newData = shouldDiscardServerPrecog ? omit(data, 'precogs') : data;

        // Important gotcha: from the server event we receive precogs embedded in the conversation object.
        // We extract them out and handle them separately.
        const conversationPrecogs =
          newData.precogs && newData.precogs.map((p: any) => parseConversationPrecog(conversationId, p));
        const conversationSentPrecogs =
          newData.sentPrecogs &&
          newData.sentPrecogs.map((p: any) => parseConversationSentPrecog(conversationId, p));

        return () => {
          dispatch(
            pushConversationPrecogsUpdatedAction(
              conversationId,
              data.updated_at,
              conversationPrecogs,
              conversationSentPrecogs,
            ),
          );
          dispatch(pushPartialConversationUpdatedAction(conversationId, newData));
        };
      },
    },
    {
      name: 'conversation_deleted2',
      handler: (data) => {
        const conversationId = Number(data && data.id);
        const joinedId = Number(data && data.joinedId);

        // If we have a joined_id, this is a merge (automatic or user initiated) and not a destroy so `conversations_joined` will handle it.
        if (joinedId) {
          return noop;
        }

        return () => dispatch(conversationDeletedAction(conversationId));
      },
    },
    {
      name: 'conversations_joined',
      handler: (data) => {
        const winningConversationId = Number(data && data.conversation_id);
        const losingConversationIds =
          (Array.isArray(data.other_ids) && data.other_ids.map((i: any) => Number(i))) || [];

        return () =>
          dispatch(pushConversationsJoinedAsyncAction(winningConversationId, losingConversationIds));
      },
    },
    {
      name: 'conversation_split',
      handler: (data) => {
        const conversationId = Number(data && data.conversation_id);
        const splitMessageDate = Number(data && data.split_message_date);
        const newLastMessage = parseConversationLastMessage(data.last_message);
        const newLastManualMessage = parseConversationLastMessage(data.last_manual_message);

        return () =>
          dispatch(
            conversationSplitAction(conversationId, newLastMessage, newLastManualMessage, splitMessageDate),
          );
      },
    },
    {
      name: 'direct_conversation_precog_updated',
      handler: (data) => {
        const conversationId = Number(data.conversation_id);

        const conversationPrecog = parseConversationPrecog(conversationId, data.precog);
        const {teammateId} = conversationPrecog;

        if (teammateId === props.user.id) {
          return noop;
        }

        return () => dispatch(updateConversationPrecog(conversationPrecog));
      },
      isDirect: true,
    },
    {
      name: 'direct_conversation_precog_deleted',
      handler: (data) => {
        const precogId = parseConversationPrecogId(data);

        if (precogId.teammateId === props.user.id) {
          return noop;
        }

        return () => dispatch(deletePrecog(precogId));
      },
      isDirect: true,
    },
    {
      name: 'activity_created',
      handler: (data) => {
        const fixedData = fixPushedActivityData(data);
        const dataGroup = readApiModel(ApiActivityDataGroup, fixedData);

        return () => dispatch(createActivityFromPushAsyncAction(dataGroup));
      },
    },
    {
      name: 'activity_updated',
      handler: (data) => {
        const fixedData = fixPushedActivityData(data);
        const dataGroup = readApiModel(ApiActivityDataGroup, fixedData);
        const activity = dataGroup.activities && dataGroup.activities[0];
        if (!activity) {
          return noop;
        }

        return () => dispatch(updateActivityFromPushAsyncAction(dataGroup));
      },
    },
    {
      name: 'activity_deleted',
      handler: (data) => () => {
        const activityId = Number(data);
        if (isFinite(activityId)) {
          dispatch(pushActivityDeletedTask(activityId));
        }
      },
    },
    {
      name: 'message_updated',
      handler: (data) => {
        const messageUid = data && data.uid;
        const messageUrl = data && data.url;
        const messageStatus = data && data.status;
        if (!messageUid || !messageUrl || !messageStatus) {
          throw new Error(`Unknown message received ${JSON.stringify(data)}`);
        }

        const rawAuthorIds = data?.author_ids;
        const authorIds = isArray(rawAuthorIds) ? rawAuthorIds : [];
        const userId = props.user.id;

        if (data.error_type && data.error_desc && authorIds.includes(userId)) {
          inAnimationFrameOrHiddenCallbackAsync(
            () => {
              subscriptionsDelegate.onDraftFailed(data.error_desc);
            },
            {source: TaskSourcesEnum.PUSHER},
          );
        }

        return () => dispatch(pushMessageUpdatedTask(messageUid, messageUrl));
      },
    },
    // TODO(tone81 2024-01-31): Remove this event handler when we can encapsulate
    // draft errors with dynamic error details.
    {
      name: 'message_send_draft_failed',
      handler: (data) => {
        const messageUid = data?.uid;
        const messageUrl = data?.url;
        const messageStatus = data?.status;
        if (!messageUid || !messageUrl || !messageStatus) {
          throw new Error(`Unknown message received ${JSON.stringify(data)}`);
        }

        const rawAuthorIds = data?.author_ids;
        const authorIds = isArray(rawAuthorIds) ? rawAuthorIds : [];
        const userId = props.user.id;

        if (data.error_type && data.error_desc && authorIds.includes(userId)) {
          inAnimationFrameOrHiddenCallbackAsync(
            () => {
              subscriptionsDelegate.onDraftFailed(data.error_desc);
            },
            {source: TaskSourcesEnum.PUSHER},
          );
        }

        return () => dispatch(pushMessageUpdatedTask(messageUid, messageUrl));
      },
    },
    {
      name: 'message_deleted',
      handler: (data) => {
        const messageUid = String(data && data.uid);
        return () => dispatch(pushMessageDeletedTask(messageUid));
      },
    },
    {
      name: 'comment_created',
      handler: (data) => {
        const dataGroup = readApiModel(ApiCommentDataGroup, data);
        const comment = dataGroup.comments && dataGroup.comments[0];

        if (!comment) {
          return noop;
        }

        // If this comment is not in an opened conversation, or not sent, dispatch now.
        const isConversationSelected = props.viewerPanelsConversationIds.includes(comment.conversationId);
        if (!isConversationSelected || comment.status !== CommentStatusesEnum.SENT) {
          return () => dispatch(dataUpdatedAction(dataGroup));
        }

        // Otherwise, delay 10s to give the "activity_created" a chance to be first.
        subscriptionsDelegate.enqueueDelayedRenderTask(() => dispatch(dataUpdatedAction(dataGroup)), 10000);
        return noop;
      },
    },
    {
      name: 'comment_updated',
      handler: (data) => {
        const dataGroup = readApiModel(ApiCommentDataGroup, data);
        const comment = dataGroup.comments && dataGroup.comments[0];
        if (!comment) {
          return noop;
        }

        return () => dispatch(dataUpdatedAction(dataGroup));
      },
    },
    {
      name: 'comment_deleted',
      handler: (data) => {
        const commentUid = String(data && data.uid);
        const deletedAt = data.deleted_at;
        if (!commentUid) {
          return noop;
        }

        return () => dispatch(commentDeletedAction(commentUid, deletedAt));
      },
    },
    {
      name: 'force_data_refresh',
      handler: () => {
        subscriptionsDelegate.refreshContent();
        return noop;
      },
    },
    {
      name: 'impersonate_request',
      handler: (data) => {
        const impersonationRequest = readApiModel(ApiImpersonationRequest, data);
        subscriptionsDelegate.onReceiveImpersonationRequest(impersonationRequest);

        return noop;
      },
    },
    {
      name: 'integration_error_updated',
      handler: (data) => {
        const parsedError = readApiModel(ApiIntegrationError, data);
        return () => dispatch(updateIntegrationErrorAction(parsedError));
      },
    },
    {
      name: 'integration_error_deleted',
      handler: (data) => {
        const integrationErrorId = Number(data);
        return () => dispatch(deleteIntegrationErrorAction(integrationErrorId));
      },
    },
    {
      name: 'custom_emoji_created',
      handler: (data) => {
        const shortcode = String(data && data.shortcode);
        if (!shortcode) {
          return noop;
        }
        return () => dispatch(createdCustomEmojiAction(shortcode));
      },
    },
    {
      name: 'custom_emoji_deleted',
      handler: (data) => {
        const shortcode = String(data && data.shortcode);
        if (!shortcode) {
          return noop;
        }
        return () => dispatch(deletedCustomEmojiAction(shortcode));
      },
    },
    {
      name: 'sync_updated',
      handler: (data) => {
        // We do not need to update the integration, as that will already happen from 'integration_updated' event
        const {crmSync} = readApiModel(ApiCrmSync, data);
        return () => dispatch(dataUpdatedAction({crmSyncs: [crmSync]}));
      },
    },
    {
      name: 'sync_deleted',
      handler: (data) => {
        const crmSyncId = Number(data);
        return () => dispatch(deleteCrmSyncAction(crmSyncId));
      },
    },
    {
      name: 'topic_updated',
      handler: (data) => {
        const topic = readApiModel(ApiTopic, data);
        return () => dispatch(updateTopicAction(topic));
      },
    },
    {
      name: 'application_installation_updated',
      handler: (data) => {
        const application = readApiModel(ApiApplication, data);
        return () => dispatch(updateApplicationAction(application));
      },
    },
    {
      name: 'application_installation_deleted',
      handler: (data) => {
        const applicationId = Number(data);
        return () => dispatch(deleteApplicationAction(applicationId));
      },
    },
    {
      name: 'lens_updated',
      handler: (data) => {
        const lens = readApiModel(ApiLens, data);
        return () => dispatch(updateLensAction(lens));
      },
    },
    {
      name: 'lens_deleted',
      handler: (data) => {
        const lensId = Number(data);
        return () => dispatch(deleteLensAction(lensId));
      },
    },
    {
      name: 'macro_updated',
      handler: (data) => {
        const macro = readApiModel(ApiMacro, data);
        return () => dispatch(dataUpdatedAction({macros: [macro]}));
      },
    },
    {
      name: 'macro_deleted',
      handler: (data) => {
        const macroId = Number(data);
        return () => dispatch(deleteMacroAction(macroId));
      },
    },
    {
      name: 'macro_trigger_completed',
      handler: (data) => {
        const macroExecutionInfo = parseMacroExecutionInfo(data);
        return () => dispatch(showMacroSuccessBannerAction(macroExecutionInfo));
      },
    },
    {
      name: 'application_entry_point_authentication_status_updated',
      handler: (data) => {
        const applicationEntryPointAuthStatus = readApiModel(
          ApiApplicationEntryPointAuthenticationStatus,
          data,
        );
        return () =>
          dispatch(updateApplicationEntryPointAuthenticationStatusAction(applicationEntryPointAuthStatus));
      },
    },
    {
      name: 'knowledge_base_created',
      handler: (data) => {
        const knowledgeBase = parseKnowledgeBase(data);
        return () => dispatch(knowledgeBaseCreatedAction(knowledgeBase));
      },
    },
    {
      name: 'knowledge_base_updated',
      handler: (data) => {
        const knowledgeBase = parseKnowledgeBase(data);
        return () => dispatch(knowledgeBaseUpdatedAction(knowledgeBase));
      },
    },
    {
      name: 'ai_tagging_sample_approved',
      handler: (_data) => noop,
    },
    {
      name: 'lms_course_completed',
      handler: (_data) => noop,
    },
    {
      name: 'rule_bundle_created',
      handler: (data) => {
        const ruleBundle = parseRuleBundleInfoLight(data);
        return () => dispatch(pushSlaBundleUpdatedAsyncAction(ruleBundle));
      },
    },
    {
      name: 'rule_bundle_updated',
      handler: (data) => {
        const ruleBundle = parseRuleBundleInfoLight(data);
        return () => dispatch(pushSlaBundleUpdatedAsyncAction(ruleBundle));
      },
    },
    {
      name: 'client_stash_updated',
      handler: (data) => {
        const {items} = readApiModel(ApiStashPush, data);
        return () => dispatch(stashFetchedFromServerAction(items));
      },
    },
    {
      name: 'inquiry_list_updated',
      handler: (data) => {
        const conversationId = Number(data && data.conversation_id);
        return () => dispatch(fetchInquiryListsAsyncAction(conversationId));
      },
    },
    {
      name: 'inquiry_list_deleted',
      handler: (_data) => noop,
    },
  ];
}

/*
 * Helpers.
 */

/** Predicate for conversation_updated_thin events based on the current context. */
function shouldAcceptConversationUpdatedThinPushEvent(
  data: unknown,
  props: PushBaseProps,
  dispatch: Dispatch,
): boolean {
  const {shouldAlwaysSubscribeToUserFolderRoom} = props;

  try {
    const event = readApiModel(ApiConversationUpdatedThinEvent, data);
    const shouldAccept = dispatch(
      computeShouldAcceptConversationUpdatedThinEventAction(event, shouldAlwaysSubscribeToUserFolderRoom),
    );

    if (!shouldAccept) {
      // TODO: Fix Datadog typings to support read-only arrays.
      const context = {
        event_blob: event,
        cells_panels_rooms: props.cellsPanelsRooms,
      } as any;

      pushLogger.debug('Dropping conversation_updated_thin', context);
    }

    return shouldAccept;
  } catch (error) {
    reportToBugsnag(error);

    // Accept updates when we failed to parse the metadata.
    return true;
  }
}

/*
 * Shared dispatch props.
 */

// We ignore the conversation as it's not tailored to our teammate.
// TODO: Fix on the server instead of here.
function fixPushedActivityData(data: any) {
  return omit(data, 'conversation');
}
